import React, { useEffect, useState, type JSX } from 'react';
import useInput from '../../hooks/useInput';
import AuthCode from './AuthCode';
import { post } from '../../util/ApiWrapper';
import { CalculationResponse } from '../Checkout/Form/CouponForm';

interface ComponentProps {
  licenses: string;
  filters: string;
  title: string;
  subtitle: string;
  uitleg: string;
  bedankt: string;
  error_messages: string;
}

const isCouponCode = (value: string): boolean => value.trim().length === 6;

const isNotEmpty = (value: string): boolean => value.trim().length > 0;
const isOptional = (): boolean => true;

// eslint-disable-next-line max-len
const isEmail = (value: string): boolean => (/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/).test(value.toLowerCase());
const isPhone = (value: string): boolean => (/^\d{10,}$/).test(value.replace(/[\s()+\-\.]|ext/gi, ''));

// eslint-disable-next-line complexity
const DemoAanvraag = (props: ComponentProps): JSX.Element => {
  const [currentStep, setCurrentStep] = useState(1);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [licenses, setLicenses] = useState([]);
  const filters = JSON.parse(props.filters);
  const bedankt = JSON.parse(props.bedankt);
  const errorMessages = JSON.parse(props.error_messages);
  const [subjects, setSubjects] = useState([]);
  const [levels, setLevels] = useState([]);
  const [years, setYears] = useState([]);

  const [validationLicense, setValidationLicense] = useState(null);

  // eslint-disable-next-line complexity
  useEffect(() => {
    let allowedLicenses: string[] = null;

    if (filters.licenties === 'selection') {
      allowedLicenses = [...filters.kies_licenties];
    }
    const fetchedSubjects: string[] = [];
    const fetchedLevels: string[] = [];
    const fetchedYears: string[] = [];
    const fetchedLicenses: string[] = [];
    for (const [key, value] of Object.entries(JSON.parse(props.licenses))) {
      // console.log(typeof key);
      // @ts-ignore
      fetchedLicenses[key] = value;
      // @ts-ignore
      const course = value.article.course;
      if ((allowedLicenses === null || allowedLicenses.includes(key))
        && course.subject !== null
        && !(course.subject.toLowerCase() === 'alle' && filters.licenties === 'onlysubjects')) {
        if (validationLicense === null) {
          // @ts-ignore
          setValidationLicense(value.id_item);
        }
        if (!fetchedSubjects.includes(course.subject)) {
          fetchedSubjects.push(course.subject);
        }
        if (course.level !== null && !fetchedLevels.includes(course.level)) {
          fetchedLevels.push(course.level);
        }
        if (course.year !== null && !fetchedYears.includes(course.year)) {
          fetchedYears.push(course.year);
        }
      }
    }
    setLicenses(fetchedLicenses);
    setSubjects(fetchedSubjects);
    setLevels(fetchedLevels);
    setYears(fetchedYears);
    setSelectedSubject(fetchedSubjects[0]);
    setSelectedLevel(fetchedLevels[0]);
    setSelectedYear(parseInt(fetchedYears[0], 10));
  }, []);

  function CouponCodeCombineHandler(res: string): void {
    couponCodeSetHandler(res);
  }

  const {
    value: couponCode,
    isValid: couponCodeIsValid,
    valueChangeHandler: couponCodeChangeHandler,
    valueSetHandler: couponCodeSetHandler,
    inputBlurHandler: couponCodeBlurHandler,
  } = useInput(isCouponCode);
  const {
    value: firstNameValue,
    isValid: firstNameIsValid,
    hasError: firstNameHasError,
    valueChangeHandler: firstNameChangeHandler,
    inputBlurHandler: firstNameBlurHandler,
  } = useInput(isNotEmpty);
  const {
    value: middleNameValue,
    isValid: middleNameIsValid,
    valueChangeHandler: middleNameChangeHandler,
    inputBlurHandler: middleNameBlurHandler,
  } = useInput(isOptional);
  const {
    value: lastNameValue,
    isValid: lastNameIsValid,
    hasError: lastNameHasError,
    valueChangeHandler: lastNameChangeHandler,
    inputBlurHandler: lastNameBlurHandler,
  } = useInput(isNotEmpty);
  const {
    value: emailValue,
    isValid: emailIsValid,
    hasError: emailHasError,
    valueChangeHandler: emailChangeHandler,
    inputBlurHandler: emailBlurHandler,
  } = useInput(isEmail);
  const {
    value: phoneValue,
    isValid: phoneIsValid,
    hasError: phoneHasError,
    valueChangeHandler: phoneChangeHandler,
    inputBlurHandler: phoneBlurHandler,
  } = useInput(isPhone);
  const [terms, setTerms] = useState(false);
  const [termsTouched, setTermsTouched] = useState(false);

  function ToggleTerms(): void {
    setTerms(!terms);
    setTermsTouched(true);
  }

  async function checkCode(): Promise<void> {
    couponCodeBlurHandler();
    if (couponCode.length === 6) {
      setIsLoading(true);
      const isStillValid = await validateCoupon(couponCode);
      setIsLoading(false);
      if (isStillValid) {
        setCurrentStep(2);
      }
    }
  }

  async function validateCoupon(value: string = ''): Promise<boolean> {
    try {
      const response: CalculationResponse = await post<CalculationResponse>({
        controller: 'Coupon',
        method: 'validateDemoAanvraagCode',
        data: {
          couponCode: value,
          item: validationLicense,
        },
      });
      setError(response.error || response.total && response.total.errorMessage && response.total.errorMessage[0]);
      return response.total ? response.total.discountApplied : false;
    } catch (e) {
      setError('De kortingscode kon niet worden gevalideerd.');

      return false;
    }
  }

  function handleOnKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    const { key } = e;
    if (key === 'Enter' && couponCodeIsValid && !isLoading) {
      checkCode();
    }
  }

  const formIsValid = firstNameIsValid && middleNameIsValid && lastNameIsValid && emailIsValid && phoneIsValid && terms;

  async function onSubmit(event: React.SyntheticEvent): Promise<void> {
    event.preventDefault();
    setIsLoading(true);
    firstNameBlurHandler();
    middleNameBlurHandler();
    lastNameBlurHandler();
    emailBlurHandler();
    phoneBlurHandler();
    setTermsTouched(true);

    const values = {
      couponCode: couponCode,
      item: 0,
      form: {
        contractor_gender: 0,
        contractor_firstname: firstNameValue,
        contractor_middlename: middleNameValue,
        contractor_lastname: lastNameValue,
        contractor_email: emailValue,
        contractor_mobile: phoneValue,
        student_level: selectedLevel,
        student_subject: selectedSubject,
        student_year: selectedYear,
        terms: terms,
      },
    };

    const selectedLicense = licenses.filter((l: any) => (l.article
      && l.article.course.subject === values.form.student_subject
      && l.article.course.level === values.form.student_level
      && l.article.course.year.toString() === values.form.student_year.toString()))[0];
    values.item = selectedLicense.id_item;
    if (formIsValid) {
      const isStillValid = await validateCoupon(couponCode.replaceAll(',', ''));
      if (isStillValid) {
        await postOrder(values).catch((event: any) => {
          console.log('caught error after postOrder:');
          console.log(event);
        });
        setCurrentStep(3);
      }
    }
    setIsLoading(false);
  }

  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedYear, setSelectedYear] = useState(0);
  const [selectedSubject, setSelectedSubject] = useState('');

  function levelChangeHandler(e: any): void {
    setSelectedLevel(e.target.value);
  }

  function yearChangeHandler(e: any): void {
    setSelectedYear(parseInt(e.target.value, 10));
  }

  function subjectChangeHandler(e: any): void {
    setSelectedSubject(e.target.value);
  }

  /**
   * Generic ajax method, when the ajax call returns an error. The
   * error is passed to the state and displayed in a red alert on top of the page.
   */
  async function postOrder(values: any): Promise<JQuery.jqXHR> {
    try {
      return await post({
        method: 'submitOrder',
        controller: 'DemoAccount',
        data: values,
      });
    } catch (e) {
      console.log('caught errors in postOrder:', e);
    }
  }

  function translateError(e: string): string {
    if (e.includes('niet geldig') || e.includes('kan niet worden gebruikt; het mist het juiste artikel')) {
      return errorMessages['niet_geldig'] ? errorMessages['niet_geldig'] : 'Deze code is niet geldig.';
    }
    if (e.includes('niet meer geldig')) {
      return errorMessages['niet_meer_geldig'] ? errorMessages['niet_meer_geldig'] : 'Deze code is al gebruikt.';
    }
    return e;
  }

  return (
    <div className={'lyceo-oefenen__demo-aanvraag'}>
      <div className={'lyceo-oefenen__demo-aanvraag__intro'}>
        <h2>{props.title}</h2>
        <span className={'lyceo-oefenen__demo-aanvraag__subtitle'}>{props.subtitle}</span>
      </div>
      <form onSubmit={onSubmit}>
        {currentStep === 1 && <section className='container'>
          <AuthCode
            length={6}
            onChange={CouponCodeCombineHandler}
            onKeyDown={handleOnKeyDown}
            containerId='couponCode'
            inputClassName='input'
            capitalize={true}
          />

          <input
            name={'coupon-code'}
            type={'text'}
            onChange={couponCodeChangeHandler}
            onBlur={couponCodeBlurHandler}
            maxLength={6}
            value={couponCode}
          />

          {error && <div className="errors">
            <span>{translateError(error)}</span>
          </div>}

          <button
            className={'btn btn-secondary'}
            onClick={checkCode}
            type={'button'}
            disabled={!couponCodeIsValid || isLoading}>
            {isLoading && <i className="fa fa-circle-o-notch fa-spin"/>}CHECK MIJN CODE
          </button>
        </section>}
        {currentStep === 2 && <section className="information container">
          <input
            name={'first-name'}
            type={'text'}
            onChange={firstNameChangeHandler}
            onBlur={firstNameBlurHandler}
            maxLength={45}
            value={firstNameValue}
            placeholder={'Voornaam'}
          />
          <input
            name={'middle-name'}
            type={'text'}
            onChange={middleNameChangeHandler}
            onBlur={middleNameBlurHandler}
            maxLength={15}
            value={middleNameValue}
            placeholder={'Tussenvoegsel'}
          />
          <input
            name={'last-name'}
            type={'text'}
            onChange={lastNameChangeHandler}
            onBlur={lastNameBlurHandler}
            maxLength={80}
            value={lastNameValue}
            placeholder={'Achternaam'}
          />
          <input
            name={'email'}
            type={'text'}
            onChange={emailChangeHandler}
            onBlur={emailBlurHandler}
            maxLength={80}
            value={emailValue}
            placeholder={'E-mailadres'}
          />
          <input
            name={'phone'}
            type={'text'}
            onChange={phoneChangeHandler}
            onBlur={phoneBlurHandler}
            maxLength={12}
            value={phoneValue}
            placeholder={'Telefoonnummer'}
          />
          <label className="select">
            <select value={selectedLevel} onChange={levelChangeHandler}>
              <option value="" disabled>Niveau</option>
              {levels.map((level: string) => {
                return <option key={level} value={level}>{level}</option>;
              })}
            </select>
          </label>
          <label className="select">
            <select value={selectedYear} onChange={yearChangeHandler}>
              <option value="" disabled>Leerjaar</option>
              {years.map((year: string) => {
                return <option key={year} value={year}>{year}</option>;
              })}
            </select>
          </label>
          <label className="select">
            <select value={selectedSubject} onChange={subjectChangeHandler}>
              <option value="" disabled>Vak</option>
              {subjects.map((subject: string) => {
                return <option key={subject} value={subject}>{subject}</option>;
              })}
            </select>
          </label>
          <label className="checkbox">
            <input type="checkbox" checked={terms} onChange={ToggleTerms}/>
            <span className="checkmark"/>
            <span>Ik ga akkoord met <a href="/algemene-voorwaarden/" target='_blank'>de Algemene Voorwaarden</a>.</span>
          </label>

          {error && <div className="errors"><span>{error}</span></div>}
          {(firstNameHasError || lastNameHasError || emailHasError || phoneHasError || (termsTouched && !terms))
            && <div className="errors">
              <span className={'bold'}>{errorMessages['errors_title']
                ? errorMessages['errors_title']
                : 'Er is een fout gevonden bij het invullen van dit formulier:'}</span>
              <ul>
                {firstNameHasError && <li>
                  {errorMessages['firstname_empty']
                    ? errorMessages['firstname_empty']
                    : 'Voornaam is een verplicht veld en mag niet leeg zijn.'}
                </li>}
                {lastNameHasError && <li>
                  {errorMessages['lastname_empty']
                    ? errorMessages['lastname_empty']
                    : 'Achternaam is een verplicht veld en mag niet leeg zijn.'}
                </li>}
                {emailHasError && <li>
                  {errorMessages['email_invalid']
                    ? errorMessages['email_invalid']
                    : 'Vul een geldig e-mailadres in.'}
                </li>}
                {phoneHasError && <li>
                  {errorMessages['phone_invalid']
                    ? errorMessages['phone_invalid']
                    : 'Vul een geldig telefoonnummer in.'}
                </li>}
                {termsTouched && !terms && <li>
                  {errorMessages['terms_checked']
                    ? errorMessages['terms_checked']
                    : 'Accepteer de Algemene Voorwaarden.'}
                </li>}
              </ul>
            </div>}
          <button type={'submit'} className={'btn btn-secondary'} disabled={isLoading}>
            {isLoading && <i className="fa fa-circle-o-notch fa-spin"/>}DEMO ACCOUNT AANMAKEN
          </button>
        </section>}
        {currentStep === 3 && <section className='container'>
          <div dangerouslySetInnerHTML={{
            __html: bedankt.bedankt_text
              ? bedankt.bedankt_text
              : 'Bedankt voor je aanvraag!',
          }}/>
          <a href={bedankt.bedankt_button.button_url
            ? bedankt.bedankt_button.button_url
            : '/'} className={'btn btn-secondary'}>
            {bedankt.bedankt_button.button_text ? bedankt.bedankt_button.button_text : 'Naar Lyceo.nl'}
          </a>
        </section>}
      </form>
      {currentStep === 1 && <section className='container' dangerouslySetInnerHTML={{ __html: props.uitleg }}/>}
    </div>
  );
}

export default DemoAanvraag;
